import React, { useState, useEffect } from "react";
import { DataTable, Badge, Button, Pagination } from "@/components/ui";
import { fmt } from "@/lib/formatters";
import {
  Calendar, Tag, Store, FileText,
  RotateCcw, Eye, Boxes, Plus, Info
} from "lucide-react";

/**
 * PurchasesTable component
 * Renders one row per invoice (not per item) from inventory_purchases collection.
 */
export const PurchasesTable = ({
  purchases,
  onEdit,
  onView,
  onRestock,
  onReturn,
  onAdd,
  onFulfillQueue,
  dynamicSettings = {}
}) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setPage(1);
  }, [purchases]);

  const resolveName = (value, type, fallbackStr) => {
    if (!value) {
      if (fallbackStr) return String(fallbackStr);
      return type === 'vendor' ? 'Unknown Vendor' : 'Main Store';
    }
    if (typeof value === 'object') {
      return value.vendorName || value.storeName || value.name || value.title || '—';
    }
    return String(value);
  };

  const currentData = (purchases || []).slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil((purchases || []).length / itemsPerPage), 1);

  const columns = [
    {
      header: "Invoice Date",
      className: "whitespace-nowrap",
      render: (inv) => (
        <div className="flex items-center gap-1.5 text-slate-700 font-medium text-[13px] whitespace-nowrap">
          <Calendar size={12} className="text-slate-400" />
          {inv.invoiceDate || inv.createdAt
            ? new Date(inv.invoiceDate || inv.createdAt).toLocaleDateString()
            : '—'}
        </div>
      )
    },
    {
      header: "Invoice Number",
      className: "whitespace-nowrap",
      render: (inv) => {
        const pId = inv.projectId || '';
        const pName = inv.projectName || '';
        const hasProject = pId || pName;
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-brand-blue bg-blue-50 w-fit px-2 py-0.5 rounded border border-blue-100">
              <FileText size={10} />
              {inv.invoiceNumber || inv.invoiceNo || 'NO-INVOICE'}
              {inv.status === 'Queue' && (
                <span className="ml-1 text-[9px] font-bold text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded">
                  QUEUE
                </span>
              )}
            </div>
            {hasProject && (
              <div className="relative group">
                <div className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center cursor-pointer hover:bg-indigo-100 transition-colors">
                  <Info size={10} className="text-indigo-500" />
                </div>
                <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block">
                  <div className="bg-slate-800 text-white text-[10px] rounded-lg px-3 py-2 shadow-xl whitespace-nowrap min-w-[140px]">
                    {pId && <div className="font-bold text-indigo-300 tracking-wide">{pId}</div>}
                    {pName && <div className="text-slate-300 mt-0.5">{pName}</div>}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }
    },
    {
      header: "Vendor",
      className: "whitespace-nowrap",
      render: (inv) => (
        <div className="flex items-center gap-1.5 text-slate-700 font-medium text-[13px] whitespace-nowrap">
          <Tag size={12} className="text-slate-400" />
          {resolveName(inv.vendor, 'vendor', inv.vendorName)}
        </div>
      )
    },
    {
      header: "Customer Info",
      minWidth: '190px',
      render: (inv) => {
        const customer = inv.customerName || inv.customer?.name || inv.projectName || '—';
        const phone = inv.customerPhone || inv.customer?.phone || '';
        const email = inv.customerEmail || inv.customer?.email || '';
        return <div className="flex flex-col min-w-[180px]"><span className="text-[12px] font-semibold text-slate-700 truncate">{customer}</span>{phone && <span className="text-[10px] text-slate-500">{phone}</span>}{email && <span className="text-[10px] text-slate-400 truncate">{email}</span>}</div>;
      }
    },    {
      header: "Store Name",
      className: "whitespace-nowrap",
      render: (inv) => (
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-tight whitespace-nowrap">
          <Store size={10} />
          {resolveName(inv.store, 'store')}
        </div>
      )
    },
    {
      header: "Item Details",
      render: (inv) => {
        // New format: single item reference per document
        const singleItem = inv.item;
        // Old format: items array
        const items = inv.items || [];

        if (singleItem) {
          // New Purchase model format (one row per item)
          const itemName = singleItem.itemName || singleItem.name || inv.itemName || inv.name || inv.description || '—';
          const qty = inv.quantity || 0;
          const unit = singleItem.units?.unitName || '';
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span 
                  className="text-[12px] font-medium text-slate-700 truncate block max-w-[250px]"
                  title={itemName}
                >
                  {itemName}
                </span>
              </div>
              <div className="text-[10px] text-slate-400 font-medium ml-5">Qty: {qty} {unit}</div>
            </div>
          );
        }

        // Old format with items array
        const itemCount = items.length;
        const firstItem = items[0];
        const firstName = firstItem?.item?.itemName || firstItem?.item?.name || firstItem?.itemName || firstItem?.name || firstItem?.description || '—';

        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
              <span 
                className="text-[12px] font-medium text-slate-700 truncate block max-w-[250px]"
                title={itemCount > 1 ? `${itemCount} items` : firstName}
              >
                {itemCount > 1 ? `${itemCount} items` : firstName}
              </span>
            </div>
            {itemCount > 1 && (
              <div className="text-[10px] text-slate-400 font-medium ml-5">
                {firstName} + {itemCount - 1} more
              </div>
            )}
            {itemCount === 0 && (
              <div className="text-[10px] text-amber-500 font-bold ml-5">No items recorded</div>
            )}
          </div>
        );
      }
    },
    {
      header: "Order Qty",
      render: (inv) => {
        const qty = inv.item ? (inv.quantity || 0) : (inv.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
        return <span className="text-[12px] font-medium text-slate-700">{qty}</span>;
      }
    },
    {
      header: "Available Qty",
      render: (inv) => {
        const qty = inv.item ? (inv.availableQty || 0) : (inv.items || []).reduce((s, it) => s + (it.availableQty || 0), 0);
        return <span className="text-[12px] font-medium text-emerald-600">{qty}</span>;
      }
    },
    {
      header: "Doc Type",
      className: "whitespace-nowrap",
      render: (inv) => {
        const type = inv.docType || (inv.isQueue ? 'Quotation' : 'New Purchase');
        return (
          <span className={`whitespace-nowrap inline-block px-2 py-0.5 rounded text-[10px] font-medium tracking-wide border ${
            type === 'New Purchase'
              ? 'bg-orange-50 text-orange-700 border-orange-200'
              : 'bg-indigo-50 text-indigo-700 border-indigo-200'
          }`}>
            {type}
          </span>
        );
      }
    },
    {
      header: "Sale Type",
      render: (inv) => {
        const st = inv.saleType || inv.salesType || '—';
        const isProject = st === 'Project Sale' || st === 'Project';
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium tracking-wide border ${
            st === '—' 
              ? 'bg-slate-50 text-slate-500 border-slate-200'
              : isProject 
                ? 'bg-blue-50 text-blue-700 border-blue-200/60' 
                : 'bg-purple-50 text-purple-700 border-purple-200/60'
          }`}>
            {st}
          </span>
        );
      }
    },
    {
      header: "Tax Info",
      render: (inv) => {
        let orderQty = 0;
        let availQty = 0;
        if (inv.item) {
            orderQty = inv.quantity || 0;
            availQty = inv.availableQty || 0;
        } else {
            orderQty = (inv.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
            availQty = (inv.items || []).reduce((s, it) => s + (it.availableQty || 0), 0);
        }
        const ratio = orderQty > 0 ? (availQty / orderQty) : 1;
        const effectiveTaxable = (inv.taxableAmount > 0) ? inv.taxableAmount : (inv.totalBaseAmount || inv.netBill || 0);

        return (
          <div className="flex flex-col gap-1 text-xs min-w-[120px]">
            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-400 font-medium">Taxable</span>
              <span className="font-bold text-slate-700">{fmt(effectiveTaxable * ratio)}</span>
            </div>
            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-400 font-medium italic">Total Tax</span>
              <span className="font-bold text-slate-900">{fmt((inv.totalTax || 0) * ratio)}</span>
            </div>
          </div>
        );
      }
    },
    {
      header: "Financials",
      className: "bg-slate-50/30",
      render: (inv) => {
        let orderQty = 0;
        let availQty = 0;
        if (inv.item) {
            orderQty = inv.quantity || 0;
            availQty = inv.availableQty || 0;
        } else {
            orderQty = (inv.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
            availQty = (inv.items || []).reduce((s, it) => s + (it.availableQty || 0), 0);
        }
        const ratio = orderQty > 0 ? (availQty / orderQty) : 1;
        
        const originalAmount = inv.finalNetPayment || inv.netBill || inv.totalBaseAmount || 0;
        const amount = originalAmount * ratio;

        return (
          <div className="flex flex-col gap-0.5 items-end pr-4">
            <div className="text-[9px] font-medium text-slate-400 uppercase tracking-widest">NET BILL</div>
            <div className="text-[13px] font-medium text-brand-blue">
              {fmt(amount)}
            </div>
          </div>
        );
      }
    },
    {
      header: "Actions",
      className: "w-24",
      render: (inv) => (
        <div className="flex gap-1 items-center">
          {/* Return button — hidden when onAdd is provided (Budget context) */}
          {/* <Button
            variant="ghost"
            size="icon-xs"
            className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
            onClick={() => onReturn && onReturn(inv)}
            title="Initiate Return"
          >
            <RotateCcw size={14} />
          </Button> */}
          {onAdd ? (
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-brand-blue hover:text-blue-700 hover:bg-blue-50"
              onClick={() => onAdd(inv)}
              title="Add to Quotation"
            >
              <Plus size={16} />
            </Button>
          ) : (
            <>
              {(inv.status === 'Queue' || inv.isQueue) && onFulfillQueue && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 bg-emerald-50/50 border border-emerald-200"
                  onClick={() => onFulfillQueue(inv)}
                  title="Fulfill Queue Item & Add to Sales"
                >
                  <Plus size={16} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon-xs"
                className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                onClick={() => onReturn && onReturn(inv)}
                title="Initiate Return"
              >
                <RotateCcw size={14} />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => onView && onView(inv)}
            title="View Details"
          >
            <Eye size={14} />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="card rounded-none overflow-hidden shadow-sm border-slate-100">
      <DataTable columns={columns} data={currentData} noScroll={true} />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
};
